// @ts-check
/**
 * Submissions API functions
 *
 * Response Types
 * @typedef {import('./types').SubmissionCreateResponse} SubmissionCreateResponse
 * @typedef {import('./types').SubmissionFeedbackResponse} SubmissionFeedbackResponse
 * @typedef {import('./types').SubmissionsListResponse} SubmissionsListResponse
 * @typedef {import('./types').SubmissionsListData} SubmissionsListData
 * @typedef {import('./types').SubmissionAudioUpdateResponse} SubmissionAudioUpdateResponse
 * @typedef {import('./types').SubmissionAudioFeedbackUpdateResponse} SubmissionAudioFeedbackUpdateResponse
 * @typedef {import('./types').SubmissionAudioFeedbackDeleteResponse} SubmissionAudioFeedbackDeleteResponse
 *
 * Request Types
 * @typedef {import('./types').SubmissionCreateRequest} SubmissionCreateRequest
 * @typedef {import('./types').SubmissionFeedbackRequest} SubmissionFeedbackRequest
 * @typedef {import('./types').SubmissionAudioUpdateRequest} SubmissionAudioUpdateRequest
 */
import { apiRequest } from './base';
import { uploadAudioFile, uploadProjectVideo } from './oss';
import { PROJECT_VIDEO_ITEM_ID } from '../../utils/itemTypes';
import { logger } from '../../utils/logger';
import { normalizeCourseId } from '../../utils/courseNormalization';
import { getDisplayFeedback } from '../../utils/smartFeedback';

/** Normalize course name, falling back to original if not recognized */
function normalizeCourse(courseName) {
  return normalizeCourseId(courseName) || courseName;
}

/**
 * @param {SubmissionCreateRequest} submissionData
 * @returns {Promise<SubmissionCreateResponse>}
 */
export async function createSubmission(submissionData) {
  return apiRequest('/submissions', {
    method: 'POST',
    body: JSON.stringify(submissionData),
  });
}

/** @returns {Promise<SubmissionsListResponse>} */
export async function getSubmissions(filters = {}, options = {}) {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const endpoint = queryParams.toString() ? `/submissions?${queryParams}` : '/submissions';
  const response = await apiRequest(endpoint, options);

  // Handle legacy format (direct array) vs new format (with pagination)
  if (Array.isArray(response)) {
    return {
      data: response,
      pagination: { page: 1, limit: response.length, total: response.length, totalPages: 1 }
    };
  }

  return response;
}

function validateRating(rating) {
  if (rating === undefined || rating === null) return;
  if (!Number.isInteger(rating) || rating < 0 || rating > 1) {
    throw new Error('Rating must be 0 (fail) or 1 (pass)');
  }
}

/**
 * @param {number|string} submissionId
 * @param {number} rating
 * @returns {Promise<SubmissionFeedbackResponse>}
 */
export async function updateSubmissionRating(submissionId, rating) {
  if (!submissionId || rating === undefined || rating === null) {
    throw new Error('Submission ID and rating are required');
  }
  validateRating(rating);

  return apiRequest(`/submissions/${submissionId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ rating }),
  });
}

/**
 * @param {number|string} submissionId
 * @param {SubmissionFeedbackRequest} feedbackData
 * @returns {Promise<SubmissionFeedbackResponse>}
 */
export async function submitFeedback(submissionId, feedbackData) {
  if (!submissionId) {
    throw new Error('Submission ID is required');
  }
  if (!feedbackData || typeof feedbackData !== 'object') {
    throw new Error('Feedback data is required');
  }
  validateRating(feedbackData.rating);

  return apiRequest(`/submissions/${submissionId}/feedback`, {
    method: 'POST',
    body: JSON.stringify(feedbackData),
  });
}

/**
 * Submit smart review: fail specific words from a combined submission
 * @param {number|string} submissionId
 * @param {string[]} failedItems - e.g. ["v2", "s3", "s7"]
 * @param {string} [feedback] - Optional teacher feedback text
 */
export async function submitSmartReview(submissionId, failedItems, feedback) {
  const body = { failedItems };
  if (feedback) body.feedback = feedback;
  return apiRequest(`/submissions/${submissionId}/smart-review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

function getStudentName(user) {
  return user.name || user.fullName || user.authing_user_id;
}

/** @returns {Promise<SubmissionCreateResponse>} */
export async function submitVocabRecording({
  audioFile, user, courseName, bookNumber, lessonNumber,
  vocabIndex, vocabText, assignment_id, project_id, listenCount
}) {
  if (!audioFile) throw new Error('Audio file is required');

  const { url: audioUrl, filename } = await uploadAudioFile(
    audioFile, user.authing_user_id, courseName, bookNumber, lessonNumber, 'vocab'
  );

  logger.log('[Assignment Tracking] Submitting vocab with assignment_id:', assignment_id, 'project_id:', project_id);

  return createSubmission({
    studentId: user.authing_user_id,
    studentName: getStudentName(user),
    batch: bookNumber || '',
    lessonNumber: parseInt(lessonNumber) || 1,
    topic: normalizeCourse(courseName) || '',
    audioUrl,
    status: 'submitted',
    audioFilename: filename,
    submissionType: 'Vocab',
    sentenceId: vocabIndex,
    sentenceText: (vocabText && vocabText.length > 1000) ? vocabText.slice(0, 997) + '...' : (vocabText || ''),
    assignment_id: assignment_id ?? null,
    project_id: project_id || null,
    listen_count: listenCount ?? null,
  });
}

/** @returns {Promise<SubmissionCreateResponse>} */
export async function submitSentencesRecording({
  audioFile, user, courseName, bookNumber, lessonNumber, assignment_id
}) {
  if (!audioFile) throw new Error('Audio file is required');

  const { url: audioUrl, filename } = await uploadAudioFile(
    audioFile, user.authing_user_id, courseName, bookNumber, lessonNumber, 'sentences'
  );

  logger.log('[Assignment Tracking] Submitting sentences with assignment_id:', assignment_id);

  return createSubmission({
    studentId: user.authing_user_id,
    studentName: getStudentName(user),
    batch: bookNumber || '',
    lessonNumber: parseInt(lessonNumber) || 1,
    topic: normalizeCourse(courseName) || '',
    audioUrl,
    status: 'submitted',
    audioFilename: filename,
    submissionType: 'Sentences',
    assignment_id: assignment_id ?? null
  });
}

/** @returns {Promise<SubmissionCreateResponse>} */
export async function submitIndividualSentence({
  audioFile, user, courseName, bookNumber, lessonNumber,
  sentenceId, sentenceText, assignment_id, project_id
}) {
  if (!audioFile) throw new Error('Audio file is required');
  if (sentenceId === null || sentenceId === undefined) {
    throw new Error('Sentence ID is required');
  }

  const { url: audioUrl, filename } = await uploadAudioFile(
    audioFile, user.authing_user_id, courseName, bookNumber, lessonNumber, `sentence-${sentenceId}`
  );

  logger.log('[Assignment Tracking] Submitting sentence with assignment_id:', assignment_id, 'project_id:', project_id);

  return createSubmission({
    studentId: user.authing_user_id,
    studentName: getStudentName(user),
    batch: bookNumber || '',
    lessonNumber: parseInt(lessonNumber) || 1,
    topic: normalizeCourse(courseName) || '',
    audioUrl,
    status: 'submitted',
    audioFilename: filename,
    submissionType: 'Sentence',
    sentenceId,
    sentenceText: (sentenceText && sentenceText.length > 1000) ? sentenceText.slice(0, 997) + '...' : (sentenceText || ''),
    assignment_id: assignment_id ?? null,
    project_id: project_id || null
  });
}

/**
 * Self-study submission for an open-production (free spoken-answer) quiz
 * question — assignment_id/project_id are always omitted, matching the
 * existing self-study pattern (see submitVocabRecording), so it never
 * appears in a teacher's review inbox unless that class opts in via
 * `self_study_course`.
 * @returns {Promise<SubmissionCreateResponse>}
 */
export async function submitOpenProductionRecording({
  audioFile, studentId, courseName, bookNumber, lessonNumber, questionId, promptText
}) {
  if (!audioFile) throw new Error('Audio file is required');
  if (!studentId) throw new Error('Student ID is required');

  const { url: audioUrl, filename } = await uploadAudioFile(
    audioFile, studentId, courseName, bookNumber, lessonNumber, 'categories-open'
  );

  return createSubmission({
    studentId,
    batch: bookNumber || '',
    lessonNumber: parseInt(lessonNumber) || 1,
    topic: normalizeCourse(courseName) || '',
    audioUrl,
    status: 'submitted',
    audioFilename: filename,
    submissionType: 'CategoriesOpenProduction',
    sentenceId: questionId,
    sentenceText: (promptText && promptText.length > 1000) ? promptText.slice(0, 997) + '...' : (promptText || ''),
    assignment_id: null,
    project_id: null,
  });
}

/** The 总结 recording's sentenceId — not s/v/l-prefixed, which mean sentence/vocab/letter. */
export const SUMMARY_ITEM_ID = 'recap';

/**
 * 总结: the child retelling the day's story in one recording. Sent as a 16 kHz
 * WAV so the server can transcribe it; the page text rides along in
 * sentenceText so the teacher can read it next to what was heard.
 *
 * @returns {Promise<SubmissionCreateResponse>}
 */
export async function submitSummaryRecording({
  audioFile, studentId, studentName, courseName, bookNumber, lessonNumber, pageText, assignment_id,
}) {
  if (!audioFile) throw new Error('Audio file is required');
  if (!studentId) throw new Error('Student ID is required');

  const { url: audioUrl, filename } = await uploadAudioFile(
    audioFile, studentId, courseName, bookNumber, lessonNumber, 'summary'
  );

  return createSubmission({
    studentId,
    studentName: studentName || null,
    batch: bookNumber || '',
    lessonNumber: parseInt(lessonNumber) || 1,
    topic: normalizeCourse(courseName) || '',
    audioUrl,
    status: 'submitted',
    audioFilename: filename,
    submissionType: 'Summary',
    sentenceId: SUMMARY_ITEM_ID,
    sentenceText: (pageText && pageText.length > 1000) ? pageText.slice(0, 997) + '...' : (pageText || ''),
    assignment_id: assignment_id ?? null,
    project_id: null,
  });
}

/**
 * A video project: the child's one film of the whole page range. Stored like
 * any recording (the URL rides in audioUrl), on the project's first page.
 *
 * @returns {Promise<SubmissionCreateResponse>}
 */
export async function submitProjectVideo({
  videoFile, studentId, studentName, topic, batch, lessonNumber, projectName, assignment_id, project_id,
}) {
  if (!videoFile) throw new Error('Video file is required');
  if (!studentId) throw new Error('Student ID is required');

  const { url, filename } = await uploadProjectVideo(videoFile, studentId);

  return createSubmission({
    studentId,
    studentName: studentName || null,
    topic: normalizeCourse(topic) || '',
    batch: batch || '',
    lessonNumber: parseInt(lessonNumber) || 1,
    audioUrl: url,
    status: 'submitted',
    audioFilename: filename,
    submissionType: 'Video',
    sentenceId: PROJECT_VIDEO_ITEM_ID,
    sentenceText: projectName || '',
    assignment_id: assignment_id ?? null,
    project_id: project_id || null,
  });
}

/** @returns {Promise<SubmissionsListData[]>} */
export async function getUserLessonSubmissions({ studentId, courseName, bookNumber, lessonNumber }) {
  const parsedLessonNumber = parseInt(lessonNumber) || 1;
  if (!studentId || isNaN(parsedLessonNumber) || parsedLessonNumber < 1) {
    throw new Error('Student ID and lesson number are required');
  }

  const params = new URLSearchParams({
    studentId,
    lessonNumber: String(parsedLessonNumber),
    limit: String(1000),
  });
  if (courseName) params.append('courseName', normalizeCourse(courseName));
  if (bookNumber) params.append('bookNumber', bookNumber);

  const submissions = await apiRequest(`/submissions?${params.toString()}`);
  return submissions.data || submissions || [];
}

/** @returns {Promise<SubmissionsListData[]>} */
export async function getUserProjectSubmissions({ studentId, projectId }) {
  if (!studentId || !projectId) {
    throw new Error('Student ID and project ID are required');
  }

  const params = new URLSearchParams({ studentId, project_id: projectId, limit: String(1000) });
  const submissions = await apiRequest(`/submissions?${params.toString()}`);
  return submissions.data || submissions || [];
}

/** @returns {Promise<string[]>} */
export async function getPreviousFeedback({ topic, batch, lessonNumber, submissionType, sentenceId, limit = 10 }) {
  if (!topic || !batch || !lessonNumber || !submissionType) {
    throw new Error('Topic, batch, lesson number, and submission type are required');
  }

  const params = new URLSearchParams({
    topic: normalizeCourse(topic),
    batch,
    lessonNumber: String(parseInt(lessonNumber)),
    submissionType,
    limit: String(limit * 3),
    hasFeedback: 'true'
  });

  if (submissionType === 'Sentence' && sentenceId) {
    params.append('sentenceId', sentenceId);
  }

  try {
    const response = await apiRequest(`/submissions?${params.toString()}`);
    const submissions = response.data || response || [];

    // Extract unique non-empty feedback texts (decode smart review JSON)
    const seen = new Set();
    return submissions
      .map(sub => getDisplayFeedback(sub.feedback))
      .filter(feedback => {
        if (!feedback || !feedback.trim() || seen.has(feedback)) return false;
        seen.add(feedback);
        return true;
      })
      .slice(0, limit);
  } catch (error) {
    logger.error('Error fetching previous feedback:', error);
    return [];
  }
}

/** @returns {Promise<string>} */
export async function saveAudioFeedback(submissionId, audioUrl) {
  if (!submissionId || !audioUrl) {
    throw new Error('Submission ID and audio URL are required');
  }

  const response = await apiRequest(`/submissions/${submissionId}/audio-feedback`, {
    method: 'PATCH',
    body: JSON.stringify({ notes: audioUrl }),
  });
  return response.cdnUrl;
}

/**
 * Stamp the server's first-listen marker. Idempotent (server COALESCE).
 * Gate-release callers MUST await; no-gate callers may fire-and-forget.
 * @returns {Promise<{success: boolean}>}
 */
export async function markFeedbackListened(submissionId) {
  if (!submissionId) throw new Error('Submission ID is required');

  return apiRequest(`/submissions/${submissionId}/feedback-listened`, {
    method: 'POST',
  });
}

/**
 * The teacher's corrected transcript + word marks on a recording (总结) — see
 * src/shared/markup/transcriptMarkup.js. null clears it.
 *
 * @param {number|string} submissionId
 * @param {?object} markup
 * @returns {Promise<import('./types').SubmissionMarkupResponse>}
 */
export function saveSubmissionMarkup(submissionId, markup) {
  if (!submissionId) throw new Error('Submission ID is required');
  return apiRequest(`/submissions/${submissionId}/markup`, {
    method: 'PATCH',
    body: JSON.stringify({ markup }),
  });
}

/** @returns {Promise<SubmissionAudioFeedbackDeleteResponse>} */
export async function deleteAudioFeedback(submissionId) {
  if (!submissionId) throw new Error('Submission ID is required');

  return apiRequest(`/submissions/${submissionId}/audio-feedback`, {
    method: 'DELETE',
  });
}

/** @returns {Promise<SubmissionAudioUpdateResponse>} */
export async function updateSubmissionAudio({
  submissionId, audioFile, user, courseName, bookNumber, lessonNumber, sentenceId
}) {
  if (!submissionId || !audioFile) {
    throw new Error('Submission ID and audio file are required');
  }

  const submissionType = sentenceId ? `sentence-${sentenceId}` : 'vocab';

  const { url: audioUrl, filename } = await uploadAudioFile(
    audioFile, user.authing_user_id, courseName, bookNumber, lessonNumber, submissionType
  );

  return apiRequest(`/submissions/${submissionId}/audio`, {
    method: 'PATCH',
    body: JSON.stringify({
      audioUrl,
      audioFilename: filename,
      submittedAt: new Date().toISOString()
    }),
  });
}
